// The inspector host UI (DES2; F14). UI1 mounts these in its slots:
//   inspectToolbarSlot → <InspectToggle>, canvasOverlaySlot → <InspectLayer>,
//   composerChipSlot → <MentionTray>.
// <PropertySheet>, <TargetList> and <AnchoredToTarget> compose inside the layer (and the phone
// sheet); they are exported for surfaces that place them themselves. INS's InspectSession is
// the only selection state; these components keep pointer and geometry state only.
import { useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy } from '@/i18n/ns/inspect';
import { useInspectHost } from './context';
import { useAddMode } from './ui-store';
import type { InspectSession } from './types';

export type * from './types';
export { InspectLayer } from './layer';
export { MentionTray } from './tray';
export { PropertySheet, sharedEdits } from './property-sheet';
export { presentationValueLabel } from './presentation-tokens';
export { TargetList } from './target-list';
export { AnchoredToTarget } from './anchor';
export { NestPicker } from './picker';
export { InspectSheet } from './phone-sheet';
export { createDomAdapter, type NativeInspectMap } from './adapters/dom';
export { createFrameAdapter, parseCandidate, type FrameBridge } from './adapters/frame';
export { GeoClock } from './geometry';
export { useFrameInspectAdapter, frameTransform, overlayViewport } from './frame-inspect';

/**
 * The Inspect toggle (`inspectToolbarSlot`). Pressed, it fills with the inspector's colour and
 * the bar carries "Add another" and the hint, so the canvas itself stays clear.
 */
export function InspectToggle({
  session,
  label,
  phone = false,
}: {
  session: InspectSession;
  label?: string;
  phone?: boolean;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  const [addMode, setAddMode] = useAddMode(session.sessionId);
  const on = session.state === 'on' || session.state === 'arming';
  return (
    <span className="pc-inspect-bar" data-on={on || undefined}>
      <button
        type="button"
        className="pc-btn pc-inspect-toggle"
        data-variant="quiet"
        data-testid="inspect-toggle"
        aria-pressed={on}
        aria-busy={session.state === 'arming' || undefined}
        disabled={session.state === 'unavailable'}
        onClick={() => session.setOn(!on)}
      >
        {label ?? copy.toggle}
      </button>
      {on && !phone && (
        <>
          <button
            type="button"
            className="pc-btn pc-inspect-add"
            data-variant="quiet"
            aria-pressed={addMode}
            data-testid="inspect-add-another"
            onClick={() => setAddMode(!addMode)}
          >
            {copy.addAnother}
          </button>
          <span className="pc-inspect-bar-hint">{copy.hintBar}</span>
        </>
      )}
    </span>
  );
}

/** Transient pointer state the layer and the controls share (never selection state). */
export function useInspectGestures(): {
  hoverId: string | null;
  addMode: boolean;
  setAddMode: (value: boolean) => void;
  escape: () => void;
} {
  const host = useInspectHost();
  return {
    hoverId: host?.hover?.candidate.pid ?? null,
    addMode: host?.addMode ?? false,
    setAddMode: host?.setAddMode ?? (() => {}),
    escape: host?.escape ?? (() => {}),
  };
}
